import {
  getInfo,
  setInfo,
  getUserDetail,
  setUserDetail,
} from "@/utils/storage";

import { getUserInfoDetail } from "@/api/user";
import { getChatInboxSummary } from "@/api/chatInbox";
import { createChatUnreadSync } from "@/services/chat-unread-sync";

// 页面启动、父布局和子页面可能会同时刷新用户信息。
// 复用进行中的请求，避免同一时刻重复调用 getUserInfo 接口。
let userInfoRequest = null;
let userInfoGeneration = 0;
const unreadQueries = new WeakMap();

export default {
  namespaced: true,
  state() {
    return {
      //个人权证相关
      userInfo: getInfo(),
      userDetails: getUserDetail(),
      // 未读消息数（全局状态）
      unreadCount: 0,
    };
  },
  mutations: {
    //所有mutations的第一个参数都是state
    // 登录信息
    setUserInfo(state, obj) {
      if (
        state.userInfo?.token !== obj?.token ||
        state.userInfo?.userId !== obj?.userId
      ) {
        userInfoGeneration += 1;
        userInfoRequest = null;
        unreadQueries.get(state)?.invalidate();
        state.unreadCount = 0;
      }
      state.userInfo = obj;
      setInfo(obj);
    },
    // 用户详情
    setUserDetails(state, obj) {
      //privateRole-先搜公司内部角色
      // null :不是内部角色、1.业务员、2.业务部门经理、3.业务总监、4.销售商务、5.采购商务、6.商务经理、7.总经理、8.财务、9.出纳
      state.userDetails = obj;
      setUserDetail(obj);
    },
    // 设置未读消息数
    setUnreadCount(state, count) {
      state.unreadCount = count;
    },
  },
  actions: {
    /** 未读数来自独立汇总；多个窗口与全局WebSocket通知共享同一进行中的查询。 */
    refreshUnreadCount(context) {
      let query = unreadQueries.get(context.state);
      if (!query) {
        query = createChatUnreadSync({
          readIdentity: () => context.state.userInfo?.token
            ? JSON.stringify([context.state.userInfo.token, userInfoGeneration]) : "",
          request: getChatInboxSummary,
          onCount: count => context.commit("setUnreadCount", count),
        });
        unreadQueries.set(context.state, query);
      }
      return query.refresh();
    },
    logout(context) {
      //个人信息要重置
      context.commit("setUserInfo", {});
      context.commit("setUserDetails", {});
      context.commit("setUnreadCount", 0);
      // 切换商城账号时同步清理 AI 助手的 OpenWebUI token 和历史状态，避免复用上一账号会话。
      context.dispatch("openWebui/resetSession", null, { root: true });
      // 价格频道报价的可见范围绑定当前账号；退出后立即取消旧请求并清掉身份缓存。
      context.dispatch("priceChannelSession/clear", null, { root: true });
      //购物车信息要重置
      context.commit("cart/setCartList", [], { root: true });
      sessionStorage.removeItem("hasTip");
    },
    // 优先复用正在刷新或已缓存的用户信息，供页面初始化使用。
    ensureUserInfo(context) {
      if (userInfoRequest) {
        return userInfoRequest;
      }

      const loginId = context.state.userInfo?.userId;
      const detailId = context.state.userDetails?.userId;
      if (
        Object.keys(context.state.userDetails || {}).length > 0 &&
        (loginId == null || String(loginId) === String(detailId))
      ) {
        return Promise.resolve({
          code: 200,
          data: { userInfo: context.state.userDetails },
        });
      }

      return context.dispatch("getUserInfo");
    },
    // 获取用户信息
    async getUserInfo(context) {
      if (userInfoRequest) {
        return userInfoRequest;
      }

      const generation = userInfoGeneration;
      const token = context.state.userInfo?.token;
      const request = (userInfoRequest = (async () => {
        const response = await getUserInfoDetail();
        if (
          generation !== userInfoGeneration ||
          token !== context.state.userInfo?.token
        ) {
          throw Object.assign(new Error("USER_INFO_STALE"), {
            code: "USER_INFO_STALE",
          });
        }
        if (response.code === 200 && response.data) {
          context.commit("setUserDetails", response.data.userInfo ?? {});
          // 全局消息入口负责刷新完整未读，用户资料中的旧计数不覆盖聊天汇总。
          return response;
        }

        const message = response.msg || response.message || "获取用户信息失败";
        console.error("获取用户信息失败:", message);
        throw new Error(message);
      })());

      try {
        return await request;
      } catch (error) {
        if (error.code !== "USER_INFO_STALE")
          console.error("获取用户信息异常:", error);
        throw error;
      } finally {
        if (userInfoRequest === request) userInfoRequest = null;
      }
    },
  },
  getters: {},
};
